import { useEffect } from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import useEscapeKey from "@hooks/useEscapeKey";

const ConfirmActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  description = "Are you sure you want to proceed with this action?",
  cancelText = "Cancel",
  confirmText = "Confirm",
  loading = false,
  confirmButtonVariant = "primary", // "primary", "danger", "warning"
  icon = null,
  "data-testid": dataTestId = "confirm-action-modal",
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  useEscapeKey(onClose, isOpen && !loading);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  const getConfirmButtonClasses = () => {
    const baseClasses =
      "flex min-w-[100px] items-center justify-center rounded-md px-4 py-2 text-sm font-semibold transition-colors focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";

    switch (confirmButtonVariant) {
      case "danger":
        return `${baseClasses} ${colors.bgButtonDanger} ${colors.textPrimary} hover:opacity-80 ${colors.focusRingDanger}`;
      case "warning":
        return `${baseClasses} ${colors.bgWarning} ${colors.bgWarningHover} ${colors.focusRingWarning}`;
      case "primary":
      default:
        return `${baseClasses} ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.focusRingPrimary}`;
    }
  };

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-50 flex items-center justify-start sm:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`relative w-[448px] min-w-[448px] ${colors.bgModal} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Icon and Title */}
          <div className="mb-4 flex items-center justify-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center">
              {icon ? (
                icon
              ) : (
                <svg className={`h-12 w-12 ${colors.textActive}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </div>
          </div>

          {/* Title */}
          <div className="mb-4 text-center" data-testid={`${dataTestId}-header`}>
            <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2 uppercase`} data-testid={`${dataTestId}-title`}>{title}</h3>

            {/* Description */}
            <p className={`text-sm ${colors.textSecondary} whitespace-pre-line`} data-testid={`${dataTestId}-field-description-value`}>{description}</p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3 flex-row justify-center" data-testid={`${dataTestId}-footer`}>
            {/* Cancel Button */}
            <button
              data-testid={`${dataTestId}-cancel-button`}
              onClick={handleCancel}
              disabled={loading}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${colors.borderPrimary} ${colors.textSecondary} ${colors.bgSecondary} hover:${colors.bgPrimary} min-w-[100px] focus:ring-2 ${colors.focusRingPrimary} focus:outline-none disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {t("common.cancel-button")}
            </button>

            {/* Confirm Button */}
            <button
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleConfirm}
              disabled={loading}
              className={getConfirmButtonClasses()}
            >
              {loading ? (
                <svg
                  className={`mr-2 -ml-1 h-4 w-4 animate-spin ${colors.textNormal}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              {loading ? "Processing..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
